import { Block } from '../classes/Block';
import { VideoSettings } from '../components/VideoSettings';
import { CardGroup, buildBlockCardIndexMap } from './cardGroups';

export type TableRow = Array<string>;
export type TableMatrix = TableRow[];

export interface MinimalColumnDef {
  field: string;
}

export function buildTableData(
  blocksInput: Block[],
  columns: MinimalColumnDef[],
  settings: VideoSettings,
  cardGroups?: CardGroup[]
): TableMatrix {
  const result: TableMatrix = [];

  const serializedSettingsEntries: Array<string> = [];
  for (const [key, value] of Object.entries(settings)) {
    const outValue = (key === 'fonts' || key === 'logos')
      ? JSON.stringify(value ?? {})
      : (key === 'seconds_per_card' && Array.isArray(value))
        ? JSON.stringify(value)
        : String(value ?? '');
    serializedSettingsEntries.push(key, outValue);
  }

  const videoSettingsRow: TableRow = ['video_settings', ...serializedSettingsEntries];
  result.push(videoSettingsRow);

  const blockCardIndexMap =
    settings.mode === 'cards' && cardGroups && cardGroups.length > 0
      ? buildBlockCardIndexMap(cardGroups)
      : null;

  for (const block of blocksInput) {
    if (!block || !block.name) {
      // eslint-disable-next-line no-console
      console.warn('ExcelExporter: Skipping block due to missing name.');
      continue;
    }

    const blockObject: Record<string, unknown> = {
      show_block_name: block.shouldDisplayBlockName,
      block_name_font_style: block.blockNameFont,
      block_name_font_size: block.blockNameSize,
      block_name_font_weight: block.blockNameWeight,
      block_name_color: block.blockNameColor,
      block_name_alignment: block.blockNameAlignment ?? 'center',
      column_num: block.numColumns,
      distance_below_block_name: block.distanceBelowBlockName,
      label_value_gap: block.labelValueGap,
      margin_column: block.marginColumn ?? 45,
      edge_distance: block.edgeDistance ?? 0,
      distance_between_rows: block.distanceBetweenRows,
      distance_from_previous: block.distanceFromPreviousBlock,
      song_block: block.isSongBlock,
      single_cell: block.isSingleCell,
      credits_list: block.isCreditsList,
      credits_list_separator: block.separator,
      logo_block: block.isLogoBlock,
      sbs_blocks: block.sbsBlocks ?? 'normal',
      sbs_distance: block.sbsDistance ?? 0,
      label_font_name: block.labelFontName,
      label_font_size: block.labelFontSize,
      label_font_weight: block.labelFontWeight,
      label_align: block.labelAlign,
      label_vertical_align: block.labelVerticalAlign,
      label_color: block.labelFontColor,
      value_font_name: block.valueFontName,
      value_font_size: block.valueFontSize,
      value_font_weight: block.valueFontWeight,
      value_align: block.valueAlign,
      value_vertical_align: block.valueVerticalAlign,
      value_color: block.valueFontColor,
      has_separate_value_formatting: block.hasSeparateValueFormatting,
      label_value_alignment: block.labelValueAlignment ?? 'horizontal',
      block_locked: block.isLocked,
    };

    if (blockCardIndexMap !== null) {
      const cardIndex = blockCardIndexMap.get(block.id);
      if (cardIndex !== undefined) {
        blockObject.card_index = cardIndex;
      }
    }

    const blockDefinitionRow: TableRow = [block.name];
    for (const [key, value] of Object.entries(blockObject)) {
      blockDefinitionRow.push(key, String(value ?? ''));
    }
    result.push(blockDefinitionRow);

    if (block.dataRows && block.dataRows.length > 0) {
      for (const dataRow of block.dataRows) {
        const actualDataValues: TableRow = [];
        for (const colDef of columns) {
          const value = (dataRow as Record<string, string | number>)[colDef.field];
          actualDataValues.push(String(value ?? ''));
        }
        result.push(['', ...actualDataValues]);
      }
    }
  }

  return result;
}

